import type { MessageKey } from './mk';

/** English — the switchable second language. Exactly the keys of mk.ts (enforced by tsc + test). */
export const en: Record<MessageKey, string> = {
  'app.panelName': 'Merchant web panel',
  'app.skipToContent': 'Skip to content',
  'app.loading': 'Loading…',
  'app.retry': 'Retry',
  'app.close': 'Close',
  'app.cancel': 'Cancel',
  'app.language': 'Language',
  'app.logout': 'Sign out',
  'app.loadMore': 'Load more',
  'app.updated': 'Updated at {time}',
  'app.notFound': 'Page not found.',
  'app.toOverview': 'Go to the overview',

  'nav.label': 'Main menu',
  'nav.overview': 'Overview',
  'nav.payments': 'Payments',
  'nav.reconciliation': 'Reconciliation',
  'nav.invoice': 'Invoice',
  'nav.counterQr': 'Counter QR',
  'nav.plan': 'Plan',
  'nav.locked': 'Part of the web panel',

  'live.connecting': 'Connecting…',
  'live.open': 'Live',
  'live.reconnecting': 'Reconnecting…',
  'live.stopped': 'Not live',

  'login.title': 'Sign in',
  'login.subtitle': 'Sign in with the phone number of your ZINQ business profile.',
  'login.phone': 'Phone number',
  'login.phoneHint': 'e.g. 071 222 222',
  'login.phoneInvalid': 'Enter a North Macedonian mobile number, e.g. 071 222 222.',
  'login.sendCode': 'Send code',
  'login.code': 'Verification code',
  'login.codeSentTo': 'Enter the 6-digit code for {phone}.',
  'login.codeInvalid': 'The code has 6 digits.',
  'login.signIn': 'Sign in',
  'login.changePhone': 'Change number',
  'login.resend': 'Send a new code',
  'login.devCode': 'Development code: {code}',
  'login.pilotCode': 'Pilot test code: {code}',
  'login.pilotFill': 'Fill in',
  'login.pilotNumbersOnly': 'Use the test number on your participant card.',
  'login.wrongCode': 'Wrong or expired code. Try again.',
  'login.tooMany': 'Too many attempts. Wait a moment and try again.',
  'login.codeExpired': 'The code has expired or was already used. Request a new code.',
  'login.tooManyCodes': 'Too many wrong attempts. Request a new code.',
  'login.noCustody': 'ZINQ never holds your money — every payment goes straight to your bank account.',

  'noMerchant.title': 'You don’t have a business profile yet',
  'noMerchant.body':
    'Set up accepting payments in the ZINQ app first: open a business profile and choose the account that receives payments. Then click “Check again”.',
  'noMerchant.retry': 'Check again',

  'error.generic': 'Something went wrong. Try again.',
  'error.network': 'Can’t reach ZINQ. Check your internet connection and try again.',
  'error.session': 'Your session has expired. Sign in again.',
  'error.dateInvalid': 'Choose a valid period.',
  'error.rangeOrder': 'The start date can’t be after the end date.',
  'error.rangeTooLong': 'The period can be at most {days} days.',
  'error.rangeTooLongGeneric': 'The period is too long. Choose a shorter one.',
  'error.tooMany': 'Too many attempts. Wait a moment and try again.',
  'error.future': 'Choose a period that is not in the future.',

  'upsell.title': 'Part of the web panel',
  'upsell.history': 'Payments and sales older than 7 days are part of the merchant web panel.',
  'upsell.export': 'CSV export is part of the merchant web panel.',
  'upsell.reconciliation':
    'Reconciliation with your bank statements is part of the merchant web panel.',
  'upsell.price': 'Web panel — {price} a month, on your monthly ZINQ invoice.',
  'upsell.cta': 'Turn on the web panel',

  'period.label': 'Period',
  'period.today': 'Today',
  'period.yesterday': 'Yesterday',
  'period.week': 'Last 7 days',
  'period.month': 'This month',
  'period.lastMonth': 'Last month',
  'period.from': 'From',
  'period.to': 'To',
  'period.show': 'Show',

  'overview.title': 'Overview',
  'overview.count': 'Payments',
  'overview.gross': 'Gross sales',
  'overview.fee': 'ZINQ fee',
  'overview.net': 'Net after fee',
  'overview.settledOnly': 'Totals include completed payments only.',
  'overview.feeNote':
    'The 0.9% fee (at least 3 MKD) is billed monthly on your ZINQ invoice — it is never deducted from payments.',
  'overview.latest': 'Latest payments',
  'overview.empty': 'No payments in this period yet.',
  'overview.seeAll': 'All payments',
  'overview.paying': '{payer} is paying {amount} — waiting for approval in their bank',
  'overview.payingAnon': 'A customer is paying {amount} — waiting for approval in their bank',
  'overview.newPayment': 'New payment: {amount} from {payer}',

  'payments.title': 'Payments',
  'payments.export': 'Export CSV',
  'payments.exporting': 'Exporting…',
  'payments.exported': 'Downloaded {file}.',
  'payments.caption': 'Payments from {from} to {to}',
  'payments.empty': 'No payments in the selected period.',
  'payments.totals': 'Completed: {count} · gross {gross} · fee {fee} · net {net}',
  'payments.col.date': 'Date and time',
  'payments.col.status': 'Status',
  'payments.col.reference': 'Payment code',
  'payments.col.payer': 'Customer',
  'payments.col.kind': 'QR code',
  'payments.col.amount': 'Amount',
  'payments.col.fee': 'Fee',
  'payments.col.net': 'Net',
  'payments.kind.STATIC': 'Counter',
  'payments.kind.DYNAMIC': 'Charge',

  'state.CREATED': 'Pending approval',
  'state.AWAITING_AUTHORIZATION': 'Pending approval',
  'state.AUTHORIZED': 'Processing',
  'state.SUBMITTED': 'Processing',
  'state.SETTLED': 'Completed',
  'state.REJECTED': 'Declined',
  'state.FAILED': 'Failed',
  'state.EXPIRED': 'Expired',
  'state.CANCELLED': 'Cancelled',

  'recon.title': 'Reconciliation',
  'recon.subtitle': 'Your ZINQ payments compared with your bank statement.',
  'recon.account': 'Receiving account: {bank} · {number}',
  'recon.rangeNote': 'The period can be at most 31 days.',
  'recon.card.settled': 'Paid with ZINQ',
  'recon.card.matched': 'Matched',
  'recon.card.pending': 'In progress',
  'recon.card.missing': 'Not on the statement',
  'recon.card.mismatch': 'Amount differs',
  'recon.card.other': 'Other incoming',
  'recon.count.one': '{count} payment',
  'recon.count.other': '{count} payments',
  'recon.status.MATCHED': 'Matched',
  'recon.status.PENDING': 'In progress',
  'recon.status.MISSING_IN_BANK': 'Not on the statement',
  'recon.status.AMOUNT_MISMATCH': 'Amount differs',
  'recon.status.OTHER_INCOMING': 'Other incoming',
  'recon.explain.MATCHED': 'The payment is on your bank statement, with the same amount.',
  'recon.explain.PENDING':
    'The customer is still approving the payment or the bank is processing it. Check again in a few minutes.',
  'recon.explain.MISSING_IN_BANK':
    'The bank confirmed the payment to ZINQ, but it is not on your statement yet. If it doesn’t show up by the next working day, contact your bank.',
  'recon.explain.AMOUNT_MISMATCH':
    'The statement shows a different amount than ZINQ. Check the payment with your bank.',
  'recon.explain.OTHER_INCOMING':
    'Money paid into your account that was not made with ZINQ, e.g. a transfer from a customer or a partner.',
  'recon.explain.linkedOther':
    'A credit carrying this ZINQ payment’s reference that is not matched to it (e.g. booked twice, or after a failed payment). Check it with your bank.',
  'recon.filter.all': 'All',
  'recon.filterLabel': 'Show status',
  'recon.legend': 'What the statuses mean',
  'recon.caption': 'Reconciliation from {from} to {to}',
  'recon.empty': 'No payments in the selected period.',
  'recon.col.status': 'Status',
  'recon.col.zinqTime': 'Paid in ZINQ',
  'recon.col.bankDate': 'Booked by the bank',
  'recon.col.who': 'Customer / payer',
  'recon.col.reference': 'Ref.',
  'recon.col.zinqAmount': 'ZINQ amount',
  'recon.col.bankAmount': 'Statement amount',
  'recon.col.net': 'Net',
  'recon.unavailable.title': 'Reconciliation is not available',
  'recon.unavailable.MOCK_RAILS':
    'ZINQ is running in demo mode without a real bank, so there is no statement to compare with.',
  'recon.unavailable.NO_CONSENT':
    'The consent to read your receiving account has expired or was withdrawn. Link the bank again in the ZINQ app.',
  'recon.unavailable.BANK_ERROR': 'The bank is not responding right now. Try again in a few minutes.',

  'invoice.title': 'Invoice',
  'invoice.month': 'Month',
  'invoice.heading': 'Monthly invoice for {month}',
  'invoice.status.OPEN': 'Open',
  'invoice.status.CLOSED': 'Closed',
  'invoice.openNote': 'The month is not over yet: the invoice grows until the end of the month.',
  'invoice.kind.P2P_FEE': 'Fees for transfers to another bank',
  'invoice.kind.PLUS_SUB': 'ZINQ Plus',
  'invoice.kind.MERCHANT_MDR': 'Fee for QR payments (0.9%, at least 3 MKD)',
  'invoice.kind.PANEL_SUB': 'Merchant web panel',
  'invoice.col.item': 'Item',
  'invoice.col.count': 'Count',
  'invoice.col.amount': 'Amount',
  'invoice.col.date': 'Date and time',
  'invoice.col.reference': 'Ref.',
  'invoice.total': 'Total',
  'invoice.items': 'Individual items',
  'invoice.itemsLimit': 'Showing up to the 500 most recent items.',
  'invoice.empty': 'No items for this month.',
  'invoice.note':
    'ZINQ never holds your money and takes nothing from payments: fees are billed on this monthly invoice.',

  'qr.title': 'Counter QR',
  'qr.subtitle':
    'Print it and put it on the counter. Customers scan it, enter the amount and approve the payment in their own bank.',
  'qr.cta': 'Scan with ZINQ to pay',
  'qr.merchantCode': 'Merchant code',
  'qr.reference': 'Payment code',
  'qr.min': 'Minimum amount: {amount}',
  'qr.noCamera': 'No camera? Type the payment code in the ZINQ app.',
  'qr.print': 'Print (A6)',
  'qr.alt': 'Payment QR code for {name}',
  'qr.never': 'This QR code never changes and never expires.',
  'qr.noFee': 'Customers pay ZINQ nothing.',
  'qr.inactive':
    'The receiving account is not active, so customers can’t pay. Choose another account in the ZINQ app.',

  'plan.title': 'Plan',
  'plan.name': 'Merchant web panel',
  'plan.price': '{price} a month',
  'plan.billing': 'Billed monthly, on your ZINQ invoice.',
  'plan.status.active': 'Active',
  'plan.status.inactive': 'Not active',
  'plan.activeSince': 'Active since {date}.',
  'plan.cancelledOn': 'Cancelled on {date}.',
  'plan.includes': 'What’s included',
  'plan.feature.reconciliation': 'Reconciliation with your bank statements',
  'plan.feature.export': 'CSV export of your payments, for accounting',
  'plan.feature.history': 'Payments and sales older than 7 days',
  'plan.freeTitle': 'Free, without the panel',
  'plan.free.recent': 'Sales and payments for today and the last 7 days',
  'plan.free.qr': 'Counter QR and charge QR codes',
  'plan.free.invoice': 'Monthly invoice',
  'plan.activate': 'Turn on the web panel',
  'plan.cancel': 'Cancel the web panel',
  'plan.confirmActivate.title': 'Turn on the web panel?',
  'plan.confirmActivate.body':
    '{price} for {month} will be added to your monthly ZINQ invoice, then every month until you cancel. If this month is already billed, it is not billed again.',
  'plan.confirmActivate.ok': 'Turn on',
  'plan.confirmCancel.title': 'Cancel the web panel?',
  'plan.confirmCancel.body':
    'Reconciliation, export and older payments stop being available right away. The month already billed is not refunded.',
  'plan.confirmCancel.ok': 'Cancel the web panel',
  'plan.confirmCancel.keep': 'Keep the panel',
  'plan.activated': 'The web panel is on.',
  'plan.cancelled': 'The web panel is cancelled.',
  'plan.billedMonths': 'Billed months',
  'plan.noBilledMonths': 'No months billed yet.',

  'research.title': 'Research — results',
  'research.subtitle': 'ZINQ validation study, 16.11–15.12.2026.',
  'research.key': 'Export key',
  'research.keyHint': 'The ZINQ team gives you the key. It is kept in this tab only.',
  'research.open': 'Open',
  'research.forget': 'Forget the key',
  'research.keyInvalid': 'Wrong key.',
  'research.study': 'Study label',
  'research.studyHint': 'Default: {label}. Moderators’ practice sessions are under {label}-practice.',
  'research.from': 'Sessions from',
  'research.to': 'to',
  'research.refresh': 'Refresh',
  'research.sessions': 'Participants',
  'research.role.USER': 'Users',
  'research.role.MERCHANT': 'Merchants',
  'research.hypotheses': 'Hypotheses',
  'research.col.metric': 'Hypothesis',
  'research.col.value': 'Result',
  'research.col.n': 'Participants (n)',
  'research.col.threshold': 'Threshold',
  'research.col.outcome': 'Outcome',
  'research.pass': 'Met',
  'research.fail': 'Not met',
  'research.noData': 'No data',
  'research.tasks': 'Tasks',
  'research.col.task': 'Task',
  'research.col.success': 'Success rate',
  'research.col.median': 'Median',
  'research.col.p90': '90th percentile',
  'research.col.target': 'Target',
  'research.sus': 'Usability (SUS)',
  'research.susNote': 'Mean SUS score (0–100) per role.',
  'research.downloads': 'Data export (CSV)',
  'research.downloadsNote': 'Anonymised data: participant code only, no names or phone numbers.',
  'research.export.sessions': 'Sessions',
  'research.export.tasks': 'Tasks',
  'research.export.answers': 'Answers',
  'research.export.events': 'Events',
  'research.downloaded': 'Downloaded {file}.',
};
